import { Input } from 'antd'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { CredentialActionModal } from './CredentialActionModal'

type Props = {
  open: boolean
  title: string
  /** Body copy explaining the consequence of revoking. */
  description: ReactNode
  okText: string
  loading?: boolean
  /** Receives the trimmed reason, or null when left blank (it is optional). */
  onConfirm: (reason: string | null) => void
  onCancel: () => void
}

/**
 * Shared revoke confirmation that captures an optional free-text reason.
 * Used by both the per-app credentials panel and the public-access
 * credentials page so the revoke UX stays identical across surfaces.
 */
export const RevokeCredentialModal = ({
  open,
  title,
  description,
  okText,
  loading,
  onConfirm,
  onCancel
}: Props) => {
  const { t } = useTranslation()

  return (
    <CredentialActionModal<string>
      open={open}
      title={title}
      description={description}
      okText={okText}
      okType="danger"
      loading={loading}
      fieldLabel={t('credentials.reasonLabel')}
      emptyValue=""
      renderField={(value, onChange) => (
        <Input.TextArea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t('credentials.reasonPlaceholder')}
          autoSize={{ minRows: 2, maxRows: 4 }}
          maxLength={500}
        />
      )}
      onConfirm={(reason) => onConfirm(reason.trim() || null)}
      onCancel={onCancel}
    />
  )
}
